import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { queryKeyes } from 'shared/queryKeys';
import { Get } from 'VesselMaster/vesselMaster.hooks';
import { Collapse } from 'reactstrap';
import classnames from "classnames";
import { hasRole } from 'utils/auth/authUtils';
import { Roles } from 'shared/constants';
import ErrorComponent from 'Components/ErrorComponent';
import { Skeleton, Stack } from '@mui/material';

interface Props {
    reportId: number;
}

// Define status types for better type safety
type ImportStatus = 'success' | 'failed' | undefined;

// Status configuration for better maintainability
const STATUS_CONFIG = {
    success: { className: 'text-success', message: 'Report Edited successfully 🎉' },
    failed: { className: 'text-danger', message: 'Sorry, but due to some errors we failed to edit this report.' },
    default: { className: 'text-muted', message: 'No edits were made' }
} as const;

const ImportStatusReport: React.FC<Props> = ({ reportId }) => {
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);

    const {
        data: importReport,
        isLoading,
        isError,
    } = useQuery(
        [queryKeyes.edit.checkImportStatus.key, reportId],
        () => Get(queryKeyes.edit.checkImportStatus.url(reportId), reportId),
        {
            staleTime: Infinity,
            enabled: !!reportId
        }
    );

    // Memoize status configuration to avoid recalculation
    const statusConfig = useMemo(() => {
        const status = importReport?.status as ImportStatus;
        return STATUS_CONFIG[status || 'default'];
    }, [importReport?.status]);

    // Memoize role check to avoid repeated calls
    const isAdmin = useMemo(() => hasRole(Roles.ES_ADMIN), []);

    const toggleAccordion = () => setIsAccordionOpen(prev => !prev);

    // Early returns for loading and error states
    if (isLoading) {
        return (
            <div className="p-3">
                <Stack spacing={1}>
                    <Skeleton variant="text" width="60%" />
                    <Skeleton variant="rectangular" height={100} />
                </Stack>
            </div>
        );
    }

    if (isError) {
        return <ErrorComponent message="Failed to fetch import status." />;
    }

    // Render accordion section for admin users
    const renderAccordion = (title: string, content: any) => {
        if (!isAdmin) return null;

        return (
            <>
                <div className="accordion">
                    <div className="accordion-item">
                        <h5 className="accordion-header">
                            <button
                                className={classnames(
                                    "accordion-button",
                                    "fw-medium",
                                    "p-2",
                                    "colour-grey",
                                    "align-middle",
                                    { collapsed: !isAccordionOpen }
                                )}
                                type="button"
                                onClick={toggleAccordion}
                                style={{ cursor: "pointer" }}
                            >
                                <p className="mb-0">{title}</p>
                            </button>
                        </h5>
                    </div>
                </div>
                <Collapse isOpen={isAccordionOpen} className="accordion-collapse">
                    <div className="accordion-body bg-light p-2 rounded">
                        <pre
                            className="mb-0"
                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
                        >
                            {
                                // eslint-disable-next-line no-restricted-syntax
                                JSON.stringify(content, null, 4)
                            }
                        </pre>
                    </div>
                </Collapse>
            </>
        );
    };

    // Render status-specific content
    const renderStatusContent = () => {
        const status = importReport?.status;

        switch (status) {
            case 'failed':
                return importReport?.errors_by_model ? (
                    <div className="mt-3">
                        <h6 className="text-danger">{STATUS_CONFIG.failed.message}</h6>
                        {renderAccordion('View Import Errors', importReport.errors_by_model)}
                    </div>
                ) : null;

            case 'success':
                return (
                    <div className="mt-3">
                        <p>{STATUS_CONFIG.success.message}</p>
                        {renderAccordion('Import Summary', importReport.import_summary)}
                    </div>
                );

            default:
                return null;
        }
    };

    return (
        <div className="p-3">
            <h5 className={`text-uppercase ${statusConfig.className}`}>
                {importReport?.status || statusConfig.message}
            </h5>
            {renderStatusContent()}
        </div>
    );
};

export default ImportStatusReport;